import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box, Flex, Grid, Label, Link, Spinner, Badge } from "theme-ui";
import { themevals } from "theme/themevals";
import P from "components/typography/P";
import H5 from "components/typography/H5";
import moment from "moment-timezone";
import { getProjectPurchaseOrders } from "api/PurchaseOrder";
import { getDispatchByPurchaseOrder } from "api/Projects";
import { trackActivity } from "api/ActivityTracker";
import UserContext from "context/User";

type DispatchRecord = {
  dispatch: any;
  purchaseOrder: any;
};

const statusBadge = (status?: number) => {
  switch (status) {
    case 0:
      return (
        <Badge as="span" variant="warning">
          Created
        </Badge>
      );
    case 1:
      return (
        <Badge as="span" variant="success">
          Dispatched
        </Badge>
      );
    case 3:
      return (
        <Badge as="span" variant="info">
          On Hold
        </Badge>
      );
    case 4:
      return (
        <Badge as="span" variant="info">
          On Site
        </Badge>
      );
    case 5:
      return (
        <Badge as="span" variant="success">
          Complete
        </Badge>
      );
    default:
      return (
        <Badge as="span" variant="warning">
          Created
        </Badge>
      );
  }
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  return moment(value).format("MMM D, YYYY h:mm A");
};

const Dispatches = ({ project }: { project: any }) => {
  const { user } = useContext<any>(UserContext);
  const [hasPurchaseOrders, setHasPurchaseOrders] = useState<boolean>(false);
  const [dispatchRecords, setDispatchRecords] = useState<DispatchRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const loggedForProjectRef = useRef<string | null>(null);

  useEffect(() => {
    if (!project?.id) {
      return;
    }
    if (loggedForProjectRef.current === project.id) {
      return;
    }

    loggedForProjectRef.current = project.id;

    void trackActivity({
      module: "projects",
      action: "view_dispatches",
      actionType: "view",
      userId: user?.id || null,
      companyId: user?.company?.id || null,
      entityType: "project",
      entityId: project.id,
      metadata: null,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
  }, [project?.id, user?.id, user?.company?.id]);

  useEffect(() => {
    const loadData = async () => {
      if (!project?.id) {
        setHasPurchaseOrders(false);
        setDispatchRecords([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const purchaseOrderRes = await getProjectPurchaseOrders({ data: project });
        const orders = Array.isArray(purchaseOrderRes) ? purchaseOrderRes : [];
        setHasPurchaseOrders(orders.length > 0);

        if (orders.length === 0) {
          setDispatchRecords([]);
          return;
        }

        const dispatchResults = await Promise.all(
          orders.map(async (po: any) => {
            try {
              const res = await getDispatchByPurchaseOrder({ reference: po.id });
              if (Array.isArray(res)) return res;
              if (res && res.id) return [res];
            } catch (error) {
              console.error("Failed to fetch dispatches for purchase order", po.id, error);
            }
            return [];
          })
        );

        const flattened: DispatchRecord[] = [];
        dispatchResults.forEach((dispatchList, index) => {
          const po = orders[index];
          dispatchList.forEach((dispatch: any) => {
            flattened.push({ dispatch, purchaseOrder: dispatch?.purchaseOrder ?? po });
          });
        });

        setDispatchRecords(flattened);
      } catch (error) {
        console.error("Failed to load project dispatch data", error);
        setHasPurchaseOrders(false);
        setDispatchRecords([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [project?.id]);

  const sortedDispatches = useMemo(() => {
    return dispatchRecords
      .slice()
      .sort((a, b) => {
        const aDate = a.dispatch?.createdAt ? new Date(a.dispatch.createdAt).getTime() : 0;
        const bDate = b.dispatch?.createdAt ? new Date(b.dispatch.createdAt).getTime() : 0;
        return bDate - aDate;
      });
  }, [dispatchRecords]);

  const renderContent = () => {
    if (loading) {
      return (
        <Flex sx={{ justifyContent: "center", paddingTop: "30px" }}>
          <Spinner size={32} strokeWidth={3} title="Loading dispatches" />
        </Flex>
      );
    }

    if (!hasPurchaseOrders) {
      return (
        <Flex sx={{ borderRadius: "6px", border: `1px solid ${themevals.colors.inputOutline}`, padding: "20px" }}>
          <P fontFamily="Poppins" color={themevals.colors.dkgrey} fontSize="14px" marginTop="0px" marginBottom="0px">
            There are no purchase orders for this project yet.
          </P>
        </Flex>
      );
    }

    if (!sortedDispatches.length) {
      return (
        <Flex sx={{ borderRadius: "6px", border: `1px solid ${themevals.colors.inputOutline}`, padding: "20px" }}>
          <P fontFamily="Poppins" color={themevals.colors.dkgrey} fontSize="14px" marginTop="0px" marginBottom="0px">
            No dispatches have been created for this project yet.
          </P>
        </Flex>
      );
    }

    return sortedDispatches.map(({ dispatch, purchaseOrder }) => {
      const dispatchLabel = ` Dispatch# GMD-${String(dispatch.sequence ?? 0).padStart(5, "0")}`;
      const poLabel = purchaseOrder?.sequence !== undefined ? `GMO-${String(purchaseOrder.sequence).padStart(5, "0")}` : "—";

      return (
        <Flex
          key={dispatch.id}
          sx={{
            flexDirection: "column",
            borderRadius: "6px",
            border: `1px solid ${themevals.colors.inputOutline}`,
            boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
            marginBottom: "20px",
          }}
        >
          <Flex sx={{ padding: "20px", borderBottom: `1px solid ${themevals.colors.inputOutline}`, alignItems: "center" }}>
            <Flex sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: 600 }}>{dispatchLabel}</Flex>
            <Box sx={{ marginLeft: "auto" }}>{statusBadge(dispatch.status)}</Box>
          </Flex>
          <Box sx={{ padding: "20px" }}>
            <Grid gap={0} columns={[1, "repeat(3, 1fr)"]} sx={{ rowGap: "16px" }}>
              <Flex sx={{ flexDirection: "column" }}>
                <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Purchase Order</Label>
                <Link
                  href={`/purchase-orders/${purchaseOrder?.id ?? ""}`}
                  target="_blank"
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "14px",
                    fontWeight: 600,
                    textDecoration: "underline",
                    marginTop: "5px",
                    color: themevals.colors.black,
                    display: "inline-block",
                    "&:hover": { color: themevals.colors.black },
                    "&:active": { color: themevals.colors.black },
                    "&:visited": { color: themevals.colors.black },
                  }}
                >
                  {poLabel}
                </Link>
              </Flex>
              <Flex sx={{ flexDirection: "column" }}>
                <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Truck IDs</Label>
                <P fontFamily="Poppins" color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                  {Array.isArray(dispatch.truckIds) && dispatch.truckIds.length ? dispatch.truckIds.join(", ") : "—"}
                </P>
              </Flex>
              <Flex sx={{ flexDirection: "column" }}>
                <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Dispatch Date</Label>
                <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                  {formatDateTime(dispatch.dispatchDate)}
                </P>
              </Flex>
            </Grid>
            <Grid gap={0} columns={[1, "repeat(3, 1fr)"]} sx={{ rowGap: "30px", marginTop: "30px" }}>
              <Flex sx={{ flexDirection: "column" }}>
                <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Dispatch Link</Label>
                <Link
                  href={`${window.location.origin}/dispatches/${dispatch?.id ?? ""}`}
                  target="_blank"
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "14px",
                    fontWeight: 600,
                    textDecoration: "underline",
                    marginTop: "5px",
                    color: themevals.colors.black,
                    display: "inline-block",
                    "&:hover": { color: themevals.colors.black },
                    "&:active": { color: themevals.colors.black },
                    "&:visited": { color: themevals.colors.black },
                  }}
                >
                  {`${window.location.origin}/dispatches/${dispatch?.id ?? ""}`}
                </Link>
              </Flex>
              <Flex sx={{ flexDirection: "column" }}>
                <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Dispatch Security Pin (PO#)</Label>
                <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                  {dispatch?.purchaseOrder?.sequence ? String(dispatch.purchaseOrder.sequence).padStart(5, "0") : "—"}
                </P>
              </Flex>
               <Flex sx={{ flexDirection: "column" }}>
                <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Completion Date</Label>
                <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                  {formatDateTime(dispatch.endDate)}
                </P>
              </Flex>
            </Grid>
          </Box>
        </Flex>
      );
    });
  };

  return (
    <Box sx={{ paddingBottom: "40px" }}>
      <H5 fontFamily="Rubik" fontSize="16px" marginLeft="0px" marginTop="0px">
        Dispatches
      </H5>
      {renderContent()}
    </Box>
  );
};

export default Dispatches;
